import {
  RA_REVIEW_TASK_STATUSES,
  RA_REVIEW_TASK_TYPES,
  type RaReviewTaskStatus,
  type RaTechnicalReviewStatus,
} from '@felix/domain';
import { Button, Modal, Select, Textarea, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../../EmptyState.js';
import { StatusBadge } from '../../../StatusBadge.js';
import table from '../../materials/Materials.module.css';
import purchasing from '../../purchasing/Purchasing.module.css';
import { NoMatch, TablePager } from '../../purchasing/table.js';
import { ReviewBadge } from './MasterTables.js';
import classes from './RaMaster.module.css';
import type { RaMasterItem, RaReviewTask } from './types.js';

/**
 * FINAL risk kütüphanesinin kıyı incelemesi (ADR 0098 §5, §6): faaliyetin teknik onayı
 * (Draft / In review / Approved / Rejected / Superseded) ve FINAL 03 karar kuyruğu (DUPLICATE,
 * HAZARD REVIEW). Otomatik birleştirme yok: karar kayıttır. Yalnız ofiste.
 */

export interface ActivityReviewInput {
  status: RaTechnicalReviewStatus;
  reviewer: string | null;
  reviewDay: string | null;
  note: string | null;
}

const clean = (value: string) => (value.trim() === '' ? null : value.trim());

/** Faaliyet çekmecesinde FINAL alanları ve teknik inceleme işlemleri. */
export function ActivityReviewPanel({
  item,
  busy,
  onReview,
}: {
  item: RaMasterItem<'activities'>;
  busy: boolean;
  onReview?: (input: ActivityReviewInput) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskMaster.review.${key}`, options);
  const final = item.final;
  const [reviewer, setReviewer] = useState('');
  const [day, setDay] = useState('');
  const [note, setNote] = useState('');
  if (!final) return null;
  const status = final.technicalReviewStatus;
  const rows: [string, string | null][] = [
    [k('node'), final.nodeCode ? `${final.nodeCode} · ${final.nodeName ?? ''}` : null],
    [
      k('applicability'),
      final.applicability
        ? `${final.applicability}${final.applicabilityReview ? ` (${k('applicabilityReview')})` : ''}`
        : null,
    ],
    [k('pmsReview'), final.pmsReview],
    [k('ptwScreening'), final.ptwScreening],
    [k('reviewQueue'), final.reviewQueue],
    [k('templateStatus'), final.templateStatus],
    [k('source'), final.source],
    [k('reviewer'), final.technicalReviewer],
    [k('reviewDay'), final.technicalReviewedOn],
    [k('note'), final.technicalReviewNote],
  ];
  const act = (next: RaTechnicalReviewStatus) =>
    onReview?.({
      status: next,
      reviewer: clean(reviewer),
      reviewDay: clean(day),
      note: clean(note),
    });
  return (
    <fieldset className={purchasing.dialogSection} aria-label={k('title')}>
      <legend>{k('title')}</legend>
      <p className={classes.hint}>
        {status ? <ReviewBadge status={status} t={t} /> : null}{' '}
        {status === 'approved' ? k('approvedNote') : k('notApprovedNote')}
      </p>
      <dl className={classes.form}>
        {rows
          .filter(([, value]) => value !== null && value !== '')
          .map(([label, value]) => (
            <div key={label}>
              <dt className={classes.sub}>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
      </dl>
      {onReview ? (
        <div className={classes.form}>
          <TextInput
            label={k('reviewer')}
            description={k('reviewerHelp')}
            maxLength={200}
            value={reviewer}
            onChange={(e) => setReviewer(e.currentTarget.value)}
          />
          <TextInput
            type="date"
            label={k('reviewDay')}
            description={k('reviewDayHelp')}
            value={day}
            onChange={(e) => setDay(e.currentTarget.value)}
          />
          <Textarea
            className={classes.span2}
            label={k('note')}
            description={k('noteHelp')}
            maxLength={1000}
            autosize
            minRows={2}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
          <div className={`${purchasing.dialogActions} ${classes.span2}`}>
            {status !== 'in_review' ? (
              <Button variant="default" loading={busy} onClick={() => act('in_review')}>
                {k('actions.in_review')}
              </Button>
            ) : null}
            {status === 'approved' ? (
              <Button variant="default" loading={busy} onClick={() => act('superseded')}>
                {k('actions.superseded')}
              </Button>
            ) : null}
            {status !== 'rejected' ? (
              <Button variant="light" color="red" loading={busy} onClick={() => act('rejected')}>
                {k('actions.rejected')}
              </Button>
            ) : null}
            {status !== 'approved' ? (
              <Button loading={busy} onClick={() => act('approved')}>
                {k('actions.approved')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </fieldset>
  );
}

type QueueFilter = { query: string; type: string; status: string };
const PAGE_SIZE = 15;

/** Karar kuyruğu sekmesi: süzgeç, tablo ve karar penceresi. */
export function ReviewQueuePanel({
  tasks,
  editable,
  onDecide,
}: {
  tasks: readonly RaReviewTask[];
  editable: boolean;
  onDecide?: (
    task: RaReviewTask,
    input: {
      status: RaReviewTaskStatus;
      decision: string | null;
      reviewer: string | null;
      reviewDay: string | null;
    },
  ) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskMaster.queue.${key}`, options);
  const [filter, setFilter] = useState<QueueFilter>({ query: '', type: 'all', status: 'open' });
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<RaReviewTask | null>(null);
  const items = useMemo(() => {
    const q = filter.query.trim().toLowerCase();
    return tasks.filter(
      (task) =>
        (filter.type === 'all' || task.issueType === filter.type) &&
        (filter.status === 'all' || task.status === filter.status) &&
        (q === '' ||
          [task.code, task.activityCode, task.task, task.reason, ...task.relatedCodes]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q))),
    );
  }, [tasks, filter]);
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const change = (patch: Partial<QueueFilter>) => {
    setFilter((f) => ({ ...f, ...patch }));
    setPage(1);
  };
  if (tasks.length === 0) {
    return <EmptyState title={k('empty.title')} description={k('empty.description')} />;
  }
  return (
    <>
      <p className={classes.hint}>{k('help')}</p>
      <div className={classes.toolbar}>
        <TextInput
          className={classes.search}
          leftSection={<IconSearch size={16} aria-hidden />}
          placeholder={k('search')}
          aria-label={k('search')}
          value={filter.query}
          onChange={(e) => change({ query: e.currentTarget.value })}
        />
        <Select
          className={classes.filter}
          aria-label={k('type')}
          allowDeselect={false}
          value={filter.type}
          data={[
            { value: 'all', label: k('allTypes') },
            ...RA_REVIEW_TASK_TYPES.map((v) => ({ value: v, label: k(`types.${v}`) })),
          ]}
          onChange={(v) => change({ type: v ?? 'all' })}
        />
        <Select
          className={classes.filter}
          aria-label={k('status')}
          allowDeselect={false}
          value={filter.status}
          data={[
            { value: 'all', label: k('allStatuses') },
            ...RA_REVIEW_TASK_STATUSES.map((v) => ({ value: v, label: k(`statuses.${v}`) })),
          ]}
          onChange={(v) => change({ status: v ?? 'all' })}
        />
      </div>
      {items.length === 0 ? (
        <NoMatch
          title={k('noMatch')}
          onClear={() => change({ query: '', type: 'all', status: 'all' })}
        />
      ) : (
        <>
          <section
            className={table.tableWrap}
            aria-label={k('title')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={`${table.table} ${purchasing.table}`}>
              <thead>
                <tr>
                  <th scope="col">{k('columns.code')}</th>
                  <th scope="col">{k('columns.type')}</th>
                  <th scope="col">{k('columns.activity')}</th>
                  <th scope="col" data-drop={2}>
                    {k('columns.reason')}
                  </th>
                  <th scope="col" data-drop={1}>
                    {k('columns.related')}
                  </th>
                  <th scope="col">{k('columns.status')}</th>
                  <th scope="col" data-drop={3}>
                    {k('columns.decision')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((task) => (
                  <tr key={task.id}>
                    <td>
                      <button
                        type="button"
                        className={classes.codeButton}
                        onClick={() => setOpen(task)}
                        aria-label={k('open', { code: task.code })}
                      >
                        {task.code}
                      </button>
                    </td>
                    <td>
                      <StatusBadge tone={task.issueType === 'duplicate' ? 'info' : 'warning'}>
                        {k(`types.${task.issueType}`)}
                      </StatusBadge>
                    </td>
                    <td className={classes.wrapCell}>
                      {task.activityName ?? task.task ?? '—'}
                      <span className={classes.sub}>{task.activityCode}</span>
                    </td>
                    <td data-drop={2} className={classes.wrapCell}>
                      {task.reason ?? '—'}
                    </td>
                    <td data-drop={1}>{task.relatedCodes.join(', ') || '—'}</td>
                    <td>
                      <StatusBadge tone={task.status === 'open' ? 'warning' : 'success'}>
                        {k(`statuses.${task.status}`)}
                      </StatusBadge>
                    </td>
                    <td data-drop={3} className={classes.wrapCell}>
                      {task.decision ?? '—'}
                      {task.reviewer ? (
                        <span className={classes.sub}>
                          {task.reviewer}
                          {task.reviewDay ? ` · ${task.reviewDay}` : ''}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <TablePager
            page={current}
            pages={pages}
            summary={t('riskMaster.showing', {
              from: (current - 1) * PAGE_SIZE + 1,
              to: Math.min(current * PAGE_SIZE, items.length),
              total: items.length,
            })}
            label={t('riskMaster.pagination')}
            onPage={setPage}
          />
        </>
      )}
      {open ? (
        <DecisionDialog
          task={open}
          editable={editable && onDecide !== undefined}
          onClose={() => setOpen(null)}
          onDecide={async (input) => {
            await onDecide?.(open, input);
            setOpen(null);
          }}
        />
      ) : null}
    </>
  );
}

function DecisionDialog({
  task,
  editable,
  onClose,
  onDecide,
}: {
  task: RaReviewTask;
  editable: boolean;
  onClose: () => void;
  onDecide: (input: {
    status: RaReviewTaskStatus;
    decision: string | null;
    reviewer: string | null;
    reviewDay: string | null;
  }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskMaster.queue.${key}`, options);
  const [decision, setDecision] = useState(task.decision ?? '');
  const [reviewer, setReviewer] = useState(task.reviewer ?? '');
  const [day, setDay] = useState(task.reviewDay ?? '');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const submit = async (status: RaReviewTaskStatus) => {
    if (status === 'closed' && decision.trim() === '') {
      setFailure(t('riskMaster.errors.DECISION_REQUIRED'));
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      await onDecide({
        status,
        decision: clean(decision),
        reviewer: clean(reviewer),
        reviewDay: clean(day),
      });
    } catch (error) {
      const code = (error as { code?: string } | null)?.code ?? 'GENERIC';
      setFailure(t(`riskMaster.errors.${code}`, { defaultValue: t('riskMaster.errors.GENERIC') }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      opened
      onClose={onClose}
      title={`${task.code} · ${k(`types.${task.issueType}`)}`}
      size="lg"
      closeButtonProps={{ 'aria-label': k('close') }}
    >
      <div className={classes.importBody}>
        <dl className={classes.form}>
          <div>
            <dt className={classes.sub}>{k('columns.activity')}</dt>
            <dd>
              {task.activityCode} · {task.activityName ?? task.task ?? ''}
            </dd>
          </div>
          <div>
            <dt className={classes.sub}>{k('columns.related')}</dt>
            <dd>{task.relatedCodes.join(', ') || '—'}</dd>
          </div>
          <div>
            <dt className={classes.sub}>{k('columns.reason')}</dt>
            <dd>{task.reason ?? '—'}</dd>
          </div>
          <div>
            <dt className={classes.sub}>{k('requiredAction')}</dt>
            <dd>{task.requiredAction ?? '—'}</dd>
          </div>
        </dl>
        <p className={classes.note}>{k('noMerge')}</p>
        <Textarea
          label={k('decision')}
          required
          readOnly={!editable}
          maxLength={2000}
          autosize
          minRows={3}
          value={decision}
          onChange={(e) => setDecision(e.currentTarget.value)}
        />
        <div className={classes.form}>
          <TextInput
            label={k('reviewer')}
            description={k('reviewerHelp')}
            readOnly={!editable}
            maxLength={200}
            value={reviewer}
            onChange={(e) => setReviewer(e.currentTarget.value)}
          />
          <TextInput
            type="date"
            label={k('reviewDay')}
            readOnly={!editable}
            value={day}
            onChange={(e) => setDay(e.currentTarget.value)}
          />
        </div>
        {failure ? (
          <p className={purchasing.error} role="alert">
            {failure}
          </p>
        ) : null}
        <div className={purchasing.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {k('close')}
          </Button>
          {editable && task.status === 'closed' ? (
            <Button variant="light" loading={busy} onClick={() => void submit('open')}>
              {k('reopen')}
            </Button>
          ) : null}
          {editable ? (
            <Button loading={busy} onClick={() => void submit('closed')}>
              {k('close_task')}
            </Button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
